import { ApiError, type TelegramAuthResponse } from '@pos/api-client';
import {
  AuthHeading,
  AuthScreen,
  Button,
  Field,
  Input,
  Notice,
  PasswordSignInFields,
  TelegramSignIn,
} from '@pos/ui';
import { useState } from 'react';
import {
  completeTelegramSignUp,
  loginErrorText,
  signIn,
  signInWithTelegram,
  telegramConfig,
  telegramErrorText,
  telegramNonce,
} from './auth';

type Registration = Extract<TelegramAuthResponse, { status: 'registration_required' }>;

/** «Войти через Telegram» against this app's server. */
export function TelegramSignInButton({
  onToken,
  label,
}: {
  onToken: (idToken: string, nonce: string) => Promise<string | null>;
  label?: string;
}) {
  return (
    <TelegramSignIn
      loadConfig={telegramConfig}
      getNonce={telegramNonce}
      onToken={onToken}
      label={label}
    />
  );
}

/**
 * Sign-in and sign-up are one door: «Войти через Telegram». A known Telegram
 * opens its account; a new one continues to «Новый магазин». Password sign-in
 * stays behind a link for accounts made before Telegram Login.
 */
export function SignIn({ newShop }: { newShop: boolean }) {
  const [mode, setMode] = useState<'telegram' | 'password'>('telegram');
  const [registration, setRegistration] = useState<Registration | null>(null);

  if (registration)
    return <NewShop registration={registration} onBack={() => setRegistration(null)} />;

  return (
    <AuthScreen
      footer={
        <button
          type="button"
          className="py-2 text-base text-primary"
          onClick={() => setMode(mode === 'telegram' ? 'password' : 'telegram')}
        >
          {mode === 'telegram' ? 'Войти по паролю (аккаунты до Telegram)' : 'Войти через Telegram'}
        </button>
      }
    >
      {mode === 'password' ? (
        <>
          <AuthHeading
            title="Вход по паролю"
            subtitle="Для аккаунтов, созданных до входа через Telegram"
          />
          <PasswordSignInFields
            onSubmit={async (login, password) => {
              try {
                await signIn(login, password);
                return null;
              } catch (e) {
                return loginErrorText(e);
              }
            }}
          />
        </>
      ) : (
        <>
          <AuthHeading
            title={newShop ? 'Новый магазин' : 'Вход для владельца'}
            subtitle={
              newShop
                ? '14 дней бесплатно, без карты. Начните со входа через Telegram.'
                : 'Продажи, склад и отчёты вашего бизнеса'
            }
          />
          <TelegramSignInButton
            onToken={async (idToken, nonce) => {
              try {
                const next = await signInWithTelegram(idToken, nonce);
                if (next) setRegistration(next);
                return null;
              } catch (e) {
                return telegramErrorText(e);
              }
            }}
          />
          <p className="text-sm text-ink-3">
            Безопасный вход через Telegram — без паролей и SMS. Номер телефона передаётся, только
            если вы разрешите. Нет аккаунта — создадим магазин после входа.
          </p>
        </>
      )}
    </AuthScreen>
  );
}

/** The second step for a new Telegram account: the shop's name. */
function NewShop({ registration, onBack }: { registration: Registration; onBack: () => void }) {
  const { profile } = registration;
  const [businessName, setBusinessName] = useState('');
  const [ownerName, setOwnerName] = useState(profile.name);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fields, setFields] = useState<Record<string, string>>({});
  const valid = businessName.trim().length >= 2 && ownerName.trim().length >= 2;

  return (
    <AuthScreen>
      <form
        className="space-y-5"
        onSubmit={async (e) => {
          e.preventDefault();
          if (!valid) return;
          setBusy(true);
          setError(null);
          setFields({});
          try {
            await completeTelegramSignUp(
              registration.registrationToken,
              businessName.trim(),
              ownerName.trim(),
            );
          } catch (err) {
            if (err instanceof ApiError && err.fields) setFields(err.fields);
            else setError(telegramErrorText(err));
          } finally {
            setBusy(false);
          }
        }}
      >
        <AuthHeading title="Новый магазин" subtitle="14 дней бесплатно, без карты" />
        <TelegramProfile
          name={profile.name}
          username={profile.username}
          photoUrl={profile.photoUrl}
          phone={profile.phone}
        />
        <Field label="Название магазина" required error={fields.businessName}>
          {(a) => (
            <Input
              {...a}
              size="lg"
              autoFocus
              value={businessName}
              onChange={(e) => setBusinessName(e.target.value)}
              placeholder="Магазин «Ромашка»"
            />
          )}
        </Field>
        <Field label="Ваше имя" required error={fields.ownerName}>
          {(a) => (
            <Input
              {...a}
              size="lg"
              autoComplete="name"
              value={ownerName}
              onChange={(e) => setOwnerName(e.target.value)}
            />
          )}
        </Field>
        {error && <Notice tone="danger">{error}</Notice>}
        <Button type="submit" variant="primary" size="lg" block loading={busy} disabled={!valid}>
          Создать магазин
        </Button>
        <button
          type="button"
          onClick={onBack}
          className="block w-full py-2 text-center text-base text-primary"
        >
          Назад
        </button>
      </form>
    </AuthScreen>
  );
}

export function TelegramProfile({
  name,
  username,
  photoUrl,
  phone,
}: {
  name: string;
  username: string | null;
  photoUrl: string | null;
  phone?: string | null;
}) {
  return (
    <div className="flex items-center gap-3 rounded-xl bg-sunken px-4 py-3">
      {photoUrl ? (
        <img
          src={photoUrl}
          alt=""
          className="size-10 rounded-full object-cover"
          referrerPolicy="no-referrer"
        />
      ) : (
        <div className="flex size-10 items-center justify-center rounded-full bg-primary-soft text-lg font-semibold text-primary">
          {name.slice(0, 1).toUpperCase()}
        </div>
      )}
      <div className="min-w-0 text-base">
        <div className="truncate font-medium">{name}</div>
        <div className="truncate text-ink-3">
          Telegram{username ? ` · @${username}` : ''}
          {phone ? ` · ${phone}` : ''}
        </div>
      </div>
    </div>
  );
}
